import { useState } from 'react';
import type { Finding, FindingCran, FindingStatus } from '../types';
import { updateFinding, promoteFinding, deleteFinding } from '../lib/api';
import { IconClose, IconCheck, IconRefresh } from '../components/icons';
import { useToast } from '../lib/toast';
import { BaseDrawer } from '../components/BaseDrawer';
import { relativeDate } from '../lib/format';

interface Props {
  finding: Finding;
  onClose: () => void;
  onSaved: (f: Finding) => void;
  onDeleted: (id: string) => void;
  onPromoted: (f: Finding, programmeId: string) => void;
}

const STATUS_OPTIONS: FindingStatus[] = ['pending', 'auto-applied', 'approved', 'rejected', 'needs-edit'];
const CRAN_OPTIONS: FindingCran[] = ['1', '2', '3'];

const STATUS_STYLE: Record<FindingStatus, string> = {
  pending: 'bg-amber-soft text-amber',
  'auto-applied': 'bg-cream-deep text-navy',
  approved: 'bg-funds-soft text-funds-deep',
  rejected: 'bg-red-50 text-red-700',
  'needs-edit': 'bg-founders-soft text-founders-deep',
};

const KIND_LABEL: Record<string, string> = {
  'new-program': 'Nouveau programme',
  'deadline-changed': 'Deadline modifiée',
  'amount-changed': 'Montant modifié',
  'eligibility-changed': 'Éligibilité modifiée',
  'link-broken': 'Lien cassé',
  'new-call': 'Nouvel appel',
  other: 'Autre',
};

/**
 * Drawer review d'un finding. Workflow :
 * - Voir Before / After / Evidence / Source URL
 * - Éditer Cran et Status (avec Notes optionnelles)
 * - Action Promote pour kind=new-program : crée le record ND - Programmes
 *   et update le finding en approved
 *
 * Pour les autres kinds (deadline-changed, amount-changed, etc.), pas
 * d'action Promote : approve = juste un status update. Le watcher Phase
 * suivante pourra apply le diff réel après approve.
 */
export function FindingEditDrawer({
  finding, onClose, onSaved, onDeleted, onPromoted,
}: Props) {
  const [draft, setDraft] = useState<Finding>(finding);
  const [saving, setSaving] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const toast = useToast();

  function setField<K extends keyof Finding>(key: K, value: Finding[K]) {
    setDraft((d) => ({ ...d, [key]: value }));
  }

  const dirty = JSON.stringify(draft) !== JSON.stringify(finding);
  const canPromote = draft.kind === 'new-program' && draft.status !== 'approved';
  const parsedProposed = (() => {
    if (!draft.proposedRecord) return null;
    if (typeof draft.proposedRecord !== 'string') return draft.proposedRecord;
    try { return JSON.parse(draft.proposedRecord); } catch { return null; }
  })();

  async function onSave() {
    if (!dirty) return;
    setSaving(true);
    setError(null);
    try {
      const { id, createdTime, findingId, runId, runAt, kind, programmeIds, appelIds, ...patch } = draft;
      void id; void createdTime; void findingId; void runId; void runAt; void kind; void programmeIds; void appelIds;
      const res = await updateFinding(finding.id, patch);
      onSaved(res.finding);
      toast.success('Finding sauvegardé', draft.findingId || '');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast.error('Sauvegarde échouée', msg);
    } finally {
      setSaving(false);
    }
  }

  async function onPromote() {
    if (!canPromote) return;
    if (!confirm('Promouvoir ce finding en programme ? Crée un record dans ND - Programmes (Draft) et marque le finding comme approved.')) return;
    setPromoting(true);
    setError(null);
    try {
      const res = await promoteFinding(finding.id);
      onPromoted(res.finding, res.programme.id);
      toast.success('Promoted', `Programme créé (${res.programme.id})`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast.error('Promote échoué', msg);
    } finally {
      setPromoting(false);
    }
  }

  async function onDelete() {
    if (!confirm(`Supprimer le finding ${finding.findingId || finding.id} ?`)) return;
    setDeleting(true);
    try {
      await deleteFinding(finding.id);
      onDeleted(finding.id);
      toast.success('Finding supprimé', '');
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error('Suppression échouée', msg);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <BaseDrawer onClose={onClose} widthClass="w-full md:w-[620px] md:max-w-[92vw]">
      {({ close }) => (
        <>
          {/* Header */}
          <div className="flex items-center justify-between gap-3 border-b border-line-soft px-5 py-4">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-wider text-ink-faint font-medium">
                Finding · cran {draft.cran || '-'} · {KIND_LABEL[draft.kind || ''] || draft.kind || 'inconnu'}
              </div>
              <div className="text-[15px] font-display tracking-tightish text-ink truncate">
                {draft.findingId || finding.id}
              </div>
              <div className="text-[11px] text-ink-faint">
                Run {draft.runId || '-'} · {draft.runAt ? relativeDate(draft.runAt) : '-'}
              </div>
            </div>
            <button onClick={close} className="h-8 w-8 grid place-items-center rounded-md hover:bg-cream-tint transition" aria-label="Fermer">
              <IconClose className="w-4 h-4 text-ink-muted" />
            </button>
          </div>

          {/* Body scrollable */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
            {/* Métadonnées */}
            <Section title="Métadonnées">
              <div className="grid grid-cols-3 gap-3 text-[12px]">
                <Meta label="Risk" value={draft.risk} />
                <Meta label="Confidence" value={draft.confidence} />
                <Meta label="Field" value={draft.field} mono />
              </div>
            </Section>

            {/* Diff Before/After (sauf new-program) */}
            {draft.kind !== 'new-program' && (
              <Section title="Diff proposé">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wider text-ink-faint font-medium mb-1">Before</div>
                    <pre className="rounded-md bg-red-50 border border-red-100 px-2.5 py-2 text-[12px] text-red-900 max-h-40 overflow-auto whitespace-pre-wrap">
                      {draft.before || <span className="italic text-ink-faint">vide</span>}
                    </pre>
                  </div>
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wider text-ink-faint font-medium mb-1">After</div>
                    <pre className="rounded-md bg-funds-soft border border-funds/30 px-2.5 py-2 text-[12px] text-funds-deep max-h-40 overflow-auto whitespace-pre-wrap">
                      {draft.after || <span className="italic text-ink-faint">vide</span>}
                    </pre>
                  </div>
                </div>
              </Section>
            )}

            {/* Proposed record (new-program) */}
            {draft.kind === 'new-program' && parsedProposed && (
              <Section title="Programme proposé">
                <pre className="rounded-md bg-cream-tint border border-line-soft px-2.5 py-2 text-[11.5px] text-ink max-h-72 overflow-auto whitespace-pre-wrap font-mono">
                  {JSON.stringify(parsedProposed, null, 2)}
                </pre>
                <p className="mt-1.5 text-[11px] text-ink-faint">
                  Le bouton <span className="font-medium">Promote</span> en bas crée un record dans
                  ND - Programmes à partir de ces champs (statut initial : Draft).
                </p>
              </Section>
            )}

            {/* Evidence + Source */}
            <Section title="Evidence et source">
              <div className="mb-2">
                <div className="text-[10.5px] uppercase tracking-wider text-ink-faint font-medium mb-1">Citation</div>
                <blockquote className="border-l-2 border-amber pl-3 text-[12.5px] text-ink-muted italic">
                  {draft.evidence || <span className="italic text-ink-faint">aucune evidence</span>}
                </blockquote>
              </div>
              {draft.sourceUrl && (
                <a href={draft.sourceUrl} target="_blank" rel="noopener noreferrer" className="inline-block text-[12px] text-founders-deep hover:underline font-mono break-all">
                  {draft.sourceUrl}
                </a>
              )}
            </Section>

            {/* Décision : cran + status + notes */}
            <Section title="Décision">
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-ink-faint font-medium mb-1">Cran</div>
                  <div className="flex gap-1.5">
                    {CRAN_OPTIONS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setField('cran', c)}
                        className={
                          'h-8 px-3 rounded-md text-[12px] font-medium border transition ' +
                          (draft.cran === c
                            ? 'bg-navy text-white border-navy'
                            : 'bg-white text-ink-muted border-line hover:bg-cream-tint')
                        }
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase tracking-wider text-ink-faint font-medium mb-1">Status</div>
                  <div className="flex flex-wrap gap-1.5">
                    {STATUS_OPTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setField('status', s)}
                        className={
                          'px-2.5 py-1 rounded-full text-[11px] font-medium border transition ' +
                          (draft.status === s
                            ? STATUS_STYLE[s] + ' border-current'
                            : 'bg-white text-ink-muted border-line hover:bg-cream-tint')
                        }
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-[10.5px] uppercase tracking-wider text-ink-faint font-medium mb-1">Notes</div>
                <textarea
                  rows={3}
                  value={draft.notes ?? ''}
                  onChange={(e) => setField('notes', e.target.value)}
                  placeholder="Commentaire libre, ambiguïté à lever, contexte de la décision"
                  className="w-full px-2.5 py-1.5 rounded-md border border-line text-[13px]"
                />
              </div>
              {draft.decidedAt && (
                <p className="mt-2 text-[11px] text-ink-faint">
                  Décidé {relativeDate(draft.decidedAt)} par {draft.decidedBy || 'inconnu'}
                </p>
              )}
            </Section>
          </div>

          {/* Footer */}
          <div className="border-t border-line-soft px-5 py-3 flex items-center justify-between gap-2 bg-cream-tint">
            <button
              onClick={onDelete}
              disabled={deleting}
              className="text-[12px] text-red-700 hover:underline disabled:opacity-50"
            >
              {deleting ? 'Suppression...' : 'Supprimer'}
            </button>
            <div className="flex items-center gap-2">
              {canPromote && (
                <button
                  onClick={onPromote}
                  disabled={promoting}
                  className="h-9 px-3 rounded-md bg-funds-deep text-white text-[13px] font-medium flex items-center gap-1.5 hover:bg-funds transition disabled:opacity-50"
                  title="Crée le programme dans ND - Programmes et approve ce finding"
                >
                  {promoting ? <IconRefresh className="w-4 h-4 animate-spin" /> : <IconCheck className="w-4 h-4" />}
                  {promoting ? 'Promotion...' : 'Promote'}
                </button>
              )}
              <button
                onClick={close}
                className="h-9 px-3 rounded-md border border-line bg-white text-[13px] text-ink-muted hover:bg-cream-tint transition"
              >
                Fermer
              </button>
              <button
                onClick={onSave}
                disabled={!dirty || saving}
                className="h-9 px-3.5 rounded-md bg-navy text-white text-[13px] font-medium flex items-center gap-1.5 hover:bg-navy-soft transition disabled:opacity-50"
              >
                {saving ? <IconRefresh className="w-4 h-4 animate-spin" /> : <IconCheck className="w-4 h-4" />}
                {saving ? 'Sauvegarde...' : 'Sauvegarder'}
              </button>
            </div>
          </div>

          {error && (
            <div className="absolute bottom-16 left-5 right-5 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
              {error}
            </div>
          )}
        </>
      )}
    </BaseDrawer>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="text-[11px] uppercase tracking-wider text-ink-faint font-medium mb-2 pb-1 border-b border-line-soft">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Meta({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wider text-ink-faint font-medium mb-0.5">{label}</div>
      <div className={'text-[12.5px] text-ink ' + (mono ? 'font-mono' : '')}>{value || <span className="text-ink-faint italic">-</span>}</div>
    </div>
  );
}
